import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Tag, Image as ImageIcon, MapPin, Palette, Star, Mail, Phone, Building2, Home } from 'lucide-react';
import { getMediaUrl } from '../utils/media';
import axiosInstance from '../services/api/axios';
import { getPresignedDownloadUrls } from '../utils/fileUtils';
import { FormModal } from './form';

const PortfolioViewModal = ({ isOpen, onClose, portfolio, designer }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState({}); // Map of s3_key to presigned URL

  const portfolioStableId =
    portfolio?.interiorDesignerPortfolioItemId ??
    portfolio?.interior_designer_portfolio_item_id ??
    portfolio?.id ??
    null;

  // Presigned GET: same S3 keys work for interior designers when the API allows their role;
  // other roles may receive null download_url until the backend grants read/presign for those keys.
  useEffect(() => {
    if (!isOpen) {
      setImageUrls({});
      return;
    }
    if (!portfolio?.images || !Array.isArray(portfolio.images)) {
      setImageUrls({});
      return;
    }

    const keySet = new Set();
    portfolio.images.forEach((img) => {
      if (typeof img === "string") {
        const s = img.trim();
        if (s && !s.startsWith("data:") && !s.startsWith("/9j/") && !s.startsWith("iVBORw0KGgo")) {
          keySet.add(s);
        }
        return;
      }
      if (img && typeof img === "object" && (img.s3_key || img.s3Key) && !img.url && !img.downloadUrl) {
        keySet.add(img.s3_key || img.s3Key);
      }
    });

    const uniqueKeys = [...keySet];
    if (uniqueKeys.length === 0) return;

    let cancelled = false;

    const fetchImageUrls = async () => {
      try {
        const presignedUrls = await getPresignedDownloadUrls(uniqueKeys, axiosInstance);
        if (cancelled) return;

        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (!downloadUrl) return;
          const s3Key =
            presigned.s3_key || presigned.s3Key || presigned.key || uniqueKeys[index];
          if (s3Key) urlMap[s3Key] = downloadUrl;
        });
        setImageUrls(urlMap);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching presigned URLs for portfolio images:", error);
        }
      }
    };

    setImageUrls({});
    setSelectedImageIndex(0);
    fetchImageUrls();
    return () => {
      cancelled = true;
    };
  }, [isOpen, portfolioStableId, portfolio?.title, portfolio?.images?.length]);

  if (!isOpen || !portfolio) return null;

  // Design themes mapping
  const designThemes = {
    1: 'Modern',
    2: 'Minimalistic',
    3: 'Traditional',
    4: 'Contemporary'
  };

  // Helper function to get image source from various formats
  const getImageSrc = (image) => {
    if (!image) return null;

    const s3KeyFromRow =
      typeof image === "string"
        ? image.trim()
        : image?.s3_key || image?.s3Key;

    if (s3KeyFromRow && imageUrls[s3KeyFromRow]) {
      return imageUrls[s3KeyFromRow];
    }

    if (typeof image === "object" && s3KeyFromRow) {
      if (image.url || image.downloadUrl) {
        return getMediaUrl(image) || image.url || image.downloadUrl;
      }
      return null;
    }

    if (typeof image === "object" && image.url) {
      return getMediaUrl(image);
    }

    if (typeof image === "string") {
      if (image.startsWith("data:image/") || image.startsWith("/9j/") || image.startsWith("iVBORw0KGgo")) {
        if (image.startsWith("/9j/")) return `data:image/jpeg;base64,${image}`;
        if (image.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${image}`;
        return image;
      }
      return null;
    }

    if (typeof image === "object" && image.base64) {
      const b = image.base64;
      if (b.startsWith("data:")) return b;
      if (b.startsWith("/9j/")) return `data:image/jpeg;base64,${b}`;
      if (b.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${b}`;
      return `data:image/jpeg;base64,${b}`;
    }

    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return price;
    return `₹${numPrice.toLocaleString('en-IN')}`;
  };

  // Sub-tags mapping (if needed)
  const subTagsMap = {
    1: 'Sustainable',
    2: 'Vastu-friendly',
    3: 'Space Saving',
    4: 'Kids Friendly',
    5: 'Budget Friendly',
    6: 'Pet Friendly',
    7: 'Premium',
    8: 'Smart Home',
    9: 'Elderly Friendly',
    10: 'WHF Setup'
  };

  const images = portfolio.images || [];
  const mainImage = images.length > 0 ? getImageSrc(images[selectedImageIndex]) : null;
  const isLoadingImages = images.some((img) => {
    const s3Key =
      typeof img === "string" && !img.startsWith("data:") && !img.startsWith("/9j/") && !img.startsWith("iVBORw0KGgo")
        ? img.trim()
        : img?.s3_key || img?.s3Key;
    if (!s3Key) return false;
    if (imageUrls[s3Key]) return false;
    if (typeof img === "object" && (img.url || img.downloadUrl)) return false;
    return true;
  });
  const themeNames = (portfolio.designThemeIds || [])
    .map((id) => designThemes[id])
    .filter(Boolean);
  
  const subTagNames = (portfolio.designSubTagIds || [])
    .map((id) => subTagsMap[id])
    .filter(Boolean);

  // Extract design metadata - helper to safely extract values
  const extractMetadataValue = (metadataObj) => {
    if (!metadataObj) return null;
    if (typeof metadataObj === 'string' || typeof metadataObj === 'number') {
      return String(metadataObj);
    }
    if (typeof metadataObj === 'object') {
      // If it's an object, get the first value
      const values = Object.values(metadataObj);
      if (values.length > 0) {
        const firstValue = values[0];
        return typeof firstValue === 'string' || typeof firstValue === 'number' 
          ? String(firstValue) 
          : null;
      }
    }
    return null;
  };

  const designMetadata = portfolio.designMetadata || {};
  const bedroomCount = designMetadata.bedroom?.f 
    ? String(designMetadata.bedroom.f) 
    : extractMetadataValue(designMetadata.bedroom);
  const kitchenInfo = designMetadata.kitchen?.d 
    ? String(designMetadata.kitchen.d) 
    : extractMetadataValue(designMetadata.kitchen);
  const bathroomInfo = designMetadata.bathroom?.s 
    ? String(designMetadata.bathroom.s) 
    : extractMetadataValue(designMetadata.bathroom);
  const livingRoomInfo = designMetadata.living_room?.['2'] 
    ? String(designMetadata.living_room['2']) 
    : extractMetadataValue(designMetadata.living_room);

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      title={portfolio.title || 'Portfolio Details'}
      size="2xl"
    >
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            {/* Left Column - Images */}
            <div className="lg:col-span-2 bg-gray-50">
              {images.length > 0 ? (
                <div className="p-6 space-y-4">
                  {/* Main Image */}
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100">
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={portfolio.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : isLoadingImages ? (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-flamingo-500 mx-auto mb-2"></div>
                          <p className="text-xs text-gray-500">Loading image...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="text-gray-300" size={48} />
                      </div>
                    )}
                    <div className="hidden w-full h-full bg-gray-100 items-center justify-center">
                      <ImageIcon className="text-gray-300" size={48} />
                    </div>
                  </div>

                  {/* Thumbnail Grid */}
                  {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-3">
                      {images.map((image, index) => {
                        const imageSrc = getImageSrc(image);
                        const sectionName = typeof image === "object" ? image?.section || "" : "";
                        const s3Key =
                          typeof image === "string" && !image.startsWith("data:")
                            ? image.trim()
                            : image?.s3_key || image?.s3Key;
                        const isImageLoading =
                          Boolean(s3Key) &&
                          !imageSrc &&
                          !(typeof image === "object" && (image?.url || image?.downloadUrl));

                        return (
                          <button
                            key={s3Key || `thumb-${index}`}
                            onClick={() => setSelectedImageIndex(index)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImageIndex === index
                                ? 'border-flamingo-500 ring-2 ring-flamingo-200'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {imageSrc ? (
                              <>
                                <img
                                  src={imageSrc}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const placeholder = e.target.nextElementSibling;
                                    if (placeholder) {
                                      placeholder.style.display = 'flex';
                                    }
                                  }}
                                />
                                <div className="hidden w-full h-full bg-gray-100 items-center justify-center">
                                  <ImageIcon className="text-gray-300" size={24} />
                                </div>
                              </>
                            ) : isImageLoading ? (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-flamingo-500"></div>
                              </div>
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <ImageIcon className="text-gray-300" size={24} />
                              </div>
                            )}
                            {sectionName && imageSrc && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                                {sectionName}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <ImageIcon className="mx-auto text-gray-300 mb-3" size={48} />
                    <p className="text-sm text-gray-400">No images available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-1 bg-white p-6 space-y-6">
              {/* Price - Most Important */}
              {portfolio.overallCost && (
                <div className="pb-4 border-b border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Overall Cost</p>
                  <p className="text-2xl font-bold text-flamingo-600">
                    {formatPrice(portfolio.overallCost)}
                  </p>
                  {portfolio.estimatedCostPerSqft && (
                    <p className="text-sm text-gray-600 mt-1">
                      {formatPrice(portfolio.estimatedCostPerSqft)} per sq.ft
                    </p>
                  )}
                </div>
              )}

              {/* Designer Info */}
              {designer && (
                <div className="pb-4 border-b border-gray-100 space-y-2">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Designer</p>
                    <p className="text-lg font-semibold text-gray-900">{designer.name || 'N/A'}</p>
                    {designer.yearsExperience && (
                      <p className="text-sm text-gray-600 mt-1">
                        {designer.yearsExperience} years experience
                      </p>
                    )}
                    {designer.specialization && (
                      <p className="text-sm text-gray-600">{designer.specialization}</p>
                    )}
                  </div>
                  
                  {/* Designer Contact Info */}
                  <div className="space-y-1.5 pt-2 border-t border-gray-100">
                    {designer.email && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Mail size={12} />
                        <span className="truncate">{designer.email}</span>
                      </div>
                    )}
                    {designer.mobile && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Phone size={12} />
                        <span>{designer.mobile}</span>
                      </div>
                    )}
                    {designer.companyName && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Building2 size={12} />
                        <span>{designer.companyName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {portfolio.description && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Description</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{portfolio.description}</p>
                </div>
              )}

              {/* Key Details */}
              <div className="space-y-3">
                {/* Property Configuration */}
                {(bedroomCount || kitchenInfo || bathroomInfo || livingRoomInfo) && (
                  <div className="pb-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Property Configuration</p>
                    <div className="grid grid-cols-2 gap-2">
                      {bedroomCount && (
                        <div className="flex items-center gap-2">
                         
                          <div>
                            <p className="text-xs text-gray-500">Bedrooms</p>
                            <p className="text-sm font-medium text-gray-900">{bedroomCount}</p>
                          </div>
                        </div>
                      )}
                      {kitchenInfo && (
                        <div className="flex items-center gap-2">
                         
                          <div>
                            <p className="text-xs text-gray-500">Kitchen</p>
                            <p className="text-sm font-medium text-gray-900">{kitchenInfo}</p>
                          </div>
                        </div>
                      )}
                      {bathroomInfo && (
                        <div className="flex items-center gap-2">
                         
                          <div>
                            <p className="text-xs text-gray-500">Bathroom</p>
                            <p className="text-sm font-medium text-gray-900">{bathroomInfo}</p>
                          </div>
                        </div>
                      )}
                      {livingRoomInfo && (
                        <div className="flex items-center gap-2">
                         
                          <div>
                            <p className="text-xs text-gray-500">Living Room</p>
                            <p className="text-sm font-medium text-gray-900">{livingRoomInfo}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Design Themes */}
                {themeNames.length > 0 && (
                  <div className="flex items-start gap-3">
                    
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1.5">Design Themes</p>
                      <div className="flex flex-wrap gap-1.5">
                        {themeNames.map((theme, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-flamingo-50 text-flamingo-700 text-xs rounded font-medium"
                          >
                            {theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sub-tags */}
                {subTagNames.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Tag className="text-gray-400 mt-0.5" size={16} />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1.5">Features</p>
                      <div className="flex flex-wrap gap-1.5">
                        {subTagNames.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Property Type */}
                {portfolio.propertyType && (
                  <div className="flex items-start gap-3">
                   
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-0.5">Property Type</p>
                      <p className="text-sm font-medium text-gray-900">{portfolio.propertyType}</p>
                    </div>
                  </div>
                )}

                {/* Site Location */}
                {portfolio.siteLocation && (
                  <div className="flex items-start gap-3">
                  
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-0.5">Site Location</p>
                      <p className="text-sm font-medium text-gray-900">{portfolio.siteLocation}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="pt-4 border-t border-gray-100 space-y-2">
                {portfolio.completedDate && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar size={14} />
                    <span>Completed {formatDate(portfolio.completedDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </FormModal>
  );
};

export default PortfolioViewModal;
